import { Button } from "@msuite/ui/button";
import type { TreeExternalApi } from "@msuite/ui/dnd";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { toast } from "@msuite/ui/toast";
import type { CalculationFormServiceItem } from "@msuite/utils/schemas";
import type { NullableFields, Tables } from "@msuite/utils/types";
import React from "react";
import { PickServiceModal } from "@/modals/organizations/pick-service-modal";

/** Props Interface */
interface CalculationFormItemsMenuProps<T, U> extends TreeExternalApi<T, U> {}

export function CalculationFormItemsMenu<T, U>({ append }: CalculationFormItemsMenuProps<T, U>) {
	/** Hooks */
	const { t } = useTranslation();
	const pickServiceModal = useDisclosure();

	/** Functions */
	async function handleOnComplete(service: NullableFields<Tables<"organizationServices">>) {
		try {
			if (!service.id) throw new Error("Service must have an ID");
			append({ ...service, positions: [{ name: "", ranking: 0 }] } as U);
		} catch {
			toast.error("Error");
		}
	}

	function handleOnAddCustomService() {
		const item: NullableFields<CalculationFormServiceItem> = {
			shortText: "Eigene Leistung",
			unitPrice: 1,
			unit: "meters",
			positions: [{ name: "", ranking: 0, meters: 1 }],
		};
		append(item as U);
	}

	function handleOnAddBlock() {
		const item: NullableFields<CalculationFormServiceItem> = {
			type: "block",
			shortText: "Block",
			quantity: 1,
		};
		append(item as U);
	}

	/** Render */
	return (
		<React.Fragment>
			<div className="h-stack justify-end">
				<Button
					icon={<Icon name="Layers" />}
					onClick={handleOnAddBlock}
					variant="white"
				>
					{t("calculations.add_block")}
				</Button>
				<Button
					icon={<Icon name="Plus" />}
					onClick={pickServiceModal.onOpen}
					variant="white"
				>
					{t("general.add")}
				</Button>
			</div>
			{pickServiceModal.isOpen && (
				<PickServiceModal
					isOpen={pickServiceModal.isOpen}
					onClose={pickServiceModal.onClose}
					onComplete={handleOnComplete}
					onCompleteCustom={handleOnAddCustomService}
				/>
			)}
		</React.Fragment>
	);
}
